import React, { useMemo, useState } from 'react';
import Papa from 'papaparse';

//Hooks
import { useDispatch } from 'react-redux';

// Actions
import { failure } from '~/redux/actions/snackbar-actions';

//Utils
import { MESSAGES } from '~/utils/message';
import request from '~/utils/request';

// Components
import OffersModal from '../OffersModal';
import OlcButton from '~/components/General/OlcButton';
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';
import OlcFormModal from '~/components/General/OlcFormModal';

//MUI Component
import Typography from '@mui/material/Typography';
import { Box, CircularProgress } from '@mui/material';

// Icons
import { GreenTickCIcon, RedCrossCIcon } from '~/assets/images';

// Styles
import './styles.scss';
import { isOfferGeneratorWebsite } from '~/utils/helperFunctions';

const OfferImportModal = (props) => {
  const {
    handleClose,
    open,
    mappingFileds,
    options,
    updateMapping,
    selectedFile,
    loadOffers,
    setLoadOffers,
  } = props;
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);

  const dispatch = useDispatch();
  const dropdownOptions = useMemo(
    () =>
      options.map((option, optionIndex) => ({
        ...option,
        id: option.id ?? option.name ?? optionIndex,
        title: option.name,
      })),
    [options]
  );
  const sampleDataByColumn = useMemo(
    () => new Map(options.map((option) => [option.name.trim(), option.examples || []])),
    [options]
  );

  const handleChange = (params, event) => {
    const updatedData = mappingFileds.map((item) => {
      if (item.label === params) {
        return { ...item, value: event.target.value, error: false };
      }
      return item;
    });
    updateMapping(updatedData);
  };

  const handleClearSelection = (params) => {
    const clearSelction = mappingFileds.map((item) => {
      if (item.label === params) {
        return { ...item, value: '' };
      }
      return item;
    });
    updateMapping(clearSelction);
  };

  const validateFields = () => {
    let inValids = [];
    const validatedData = mappingFileds.map((item) => {
      if (item.required && !item.value) {
        inValids.push(item.label);
        return { ...item, error: true };
      }
      return item;
    });
    updateMapping(validatedData);
    if (inValids.length) {
      setErrors(inValids);
      return false;
    } else {
      setErrors([]);
      return true;
    }
  };

  const getCsvRows = async (headers) => {
    return new Promise((resolve) => {
      Papa.parse(selectedFile[0], {
        header: true,
        skipEmptyLines: true,
        worker: true,
        complete: async function (results) {
          try {
            const finalData = [];

            const propAddressHeader = headers.find(
              (header) => header.dbColumn === 'propertyAddress'
            )?.csvColumn;
            const propCityHeader = headers.find(
              (header) => header.dbColumn === 'propertyCity'
            )?.csvColumn;
            const propStateHeader = headers.find(
              (header) => header.dbColumn === 'propertyState'
            )?.csvColumn;
            const propZipHeader = headers.find(
              (header) => header.dbColumn === 'propertyZip'
            )?.csvColumn;

            for (const row of results.data) {
              const propAddress = row[propAddressHeader];
              const propCity = row[propCityHeader];
              const propState = row[propStateHeader];
              const propZip = row[propZipHeader];

              if (propAddress && propCity && propState && propZip) {
                const address = `${propAddress}, ${propCity}, ${propState} ${propZip}`;

                finalData.push({
                  address,
                });
              }

              if (finalData.length >= MESSAGES.ORDERS.CREATE.MAX_RECORDS) {
                break;
              }
            }

            resolve(finalData);
          } catch (err) {
            resolve([]);
          }
        },
      });
    });
  };

  const processCsv = async () => {
    try {
      const isValid = validateFields();
      if (isValid) {
        let headers = mappingFileds.map((item) => {
          return {
            dbColumn: item.dbColumn,
            csvColumn: item.value,
          };
        });
        const addresses = await getCsvRows(headers);
        setLoading(true);
        const response = await request.post('property-addresses/fetch-estimates', {
          addresses,
        });
        setAddresses(response.data.data.addresses);
        setLoadOffers(true);
      }
    } catch (error) {
      console.log(error);
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
      return error;
    } finally {
      setLoading(false);
    }
  };

  const handleCloseBtn = () => {
    handleClose();
    setErrors([]);
    setTimeout(() => {
      setLoadOffers(false);
    }, 500);
  };

  return (
    <OlcFormModal
      open={open}
      onClose={handleCloseBtn}
      title={
        loadOffers ? 'Find Your Offer Percentage' : MESSAGES.CONTACTS.IMPORT.CONTACTS_MAPPING.TITLE
      }
      className={`offerImportMappingModal ${loadOffers ? 'offerResultsModal' : ''}`}
      footer={
        <>
          <OlcButton variant="secondary" onClick={handleCloseBtn} disabled={loading}>
            {!loadOffers
              ? MESSAGES.CONTACTS.IMPORT.CONTACTS_MAPPING.CANCEL_BUTTON
              : MESSAGES.CONTACTS.IMPORT.CONTACTS_MAPPING.CLOSE_BUTTON}
          </OlcButton>
          {!loadOffers ? (
            <OlcButton variant="primary" onClick={processCsv} disabled={loading}>
              {loading ? (
                <CircularProgress
                  sx={{
                    color: 'white',
                    width: '20px !important',
                    height: '20px !important',
                  }}
                />
              ) : (
                MESSAGES.CONTACTS.IMPORT.CONTACTS_MAPPING.SUBMIT_BUTTON
              )}
            </OlcButton>
          ) : null}
        </>
      }
    >
      {!loadOffers ? (
        <Box className="mappingTable" role="table" aria-label="CSV column mapping">
          <Box className="mappingHeader" role="row">
            <Typography role="columnheader">
              {MESSAGES.CONTACTS.IMPORT.CONTACTS_MAPPING.MATCH_LABEL}
            </Typography>
            <Typography role="columnheader">
              {isOfferGeneratorWebsite()
                ? MESSAGES.CONTACTS.IMPORT.CONTACTS_MAPPING.OUR_LABEL
                : MESSAGES.CONTACTS.IMPORT.CONTACTS_MAPPING.OLC_LABEL}
            </Typography>
            <Typography role="columnheader">
              {MESSAGES.CONTACTS.IMPORT.CONTACTS_MAPPING.CSV_LABEL}
            </Typography>
            <Typography role="columnheader">
              {MESSAGES.CONTACTS.IMPORT.CONTACTS_MAPPING.SAMPLE_LABEL}
            </Typography>
          </Box>
          <Box className="mappingRows" role="rowgroup">
            {mappingFileds.map((content) => {
              const hasError =
                content.error && errors.some((error) => error.includes(content.label));
              const examples = sampleDataByColumn.get(content.value) || [];

              return (
                <Box
                  className={`mappingRow ${
                    content.value ? 'mappingRow--verified' : hasError ? 'mappingRow--error' : ''
                  }`}
                  role="row"
                  key={content.dbColumn || content.label}
                >
                  <Box className="mappingStatus" role="cell">
                    <img
                      src={content.value ? GreenTickCIcon : RedCrossCIcon}
                      alt={content.value ? 'Mapped' : 'Not mapped'}
                    />
                  </Box>
                  <Typography className="mappingFieldLabel" role="cell">
                    {content.label}
                    {content.required ? MESSAGES.CONTACTS.IMPORT.CONTACTS_MAPPING.REQUIRED : null}
                  </Typography>
                  <Box className="mappingFieldControl" role="cell">
                    <OlcDropdownSearch
                      options={dropdownOptions}
                      selectedValue={
                        content.value ? { id: content.value, title: content.value } : null
                      }
                      setSelectedValue={(option) =>
                        option
                          ? handleChange(content.label, {
                              target: { value: option.title },
                            })
                          : handleClearSelection(content.label)
                      }
                      placeHolderText={content.label}
                      searchFieldPlaceholderText="CSV column"
                      className="mappingColumnField"
                      error={hasError}
                      clearable
                    />
                  </Box>
                  <Box className="sampleDataWrapper" role="cell">
                    <Typography title={examples.filter(Boolean).join(' / ')}>
                      {examples[0] || '—'}
                      {examples[1] ? <br /> : null}
                      {examples[1] || null}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
          {errors.length ? (
            <Box className="contactsErrorWrapper" role="alert">
              {errors.map((content) => (
                <Typography key={content} className="errorFields">
                  {MESSAGES.CONTACTS.IMPORT.CONTACTS_MAPPING.REQUIRED}
                  {content}
                  {MESSAGES.CONTACTS.IMPORT.CONTACTS_MAPPING.REQUIRED_TEXT}
                </Typography>
              ))}
            </Box>
          ) : null}
        </Box>
      ) : (
        <OffersModal addresses={addresses} />
      )}
    </OlcFormModal>
  );
};

export default OfferImportModal;
